/**
 * Query-string switches for render A/B runs (docs/PERFORMANCE.md). They exist so a
 * measurement never needs a rebuild: `?perf` opens the Debug → Perf readout, `?aa=0`
 * turns off MSAA on the canvas, `?shadebake=0` goes back to the per-frame shade Graphics
 * with its Blur filter, `?staticterrain=0` falls back to the Graphics terrain painter.
 *
 * Read once at boot (`RENDER_FLAGS`). `parseRenderFlags` is pure for tests.
 */
export type RenderFlags = {
  /** Open the Debug panel on the Perf section and publish `data-perf-*` probes. */
  perf: boolean;
  /** `antialiasGL` for the WebGL context. Default on. */
  antialias: boolean;
  /**
   * Bake the water shade into a low-res texture once instead of a per-frame Graphics
   * with a Blur filter. Default on; `?shadebake=0` keeps the legacy path for A/B.
   */
  shadeBake: boolean;
  /** Retained-geometry terrain instead of per-frame Graphics replay. Default on. */
  staticTerrain: boolean;
};

export const DEFAULT_RENDER_FLAGS: RenderFlags = {
  perf: false,
  antialias: true,
  shadeBake: true,
  staticTerrain: true,
};

function flag(params: URLSearchParams, key: string, fallback: boolean): boolean {
  if (!params.has(key)) {
    return fallback;
  }

  const raw = params.get(key)?.trim().toLowerCase() ?? '';

  if (raw === '' || raw === '1' || raw === 'true' || raw === 'on') {
    return true;
  }

  if (raw === '0' || raw === 'false' || raw === 'off') {
    return false;
  }

  return fallback;
}

export function parseRenderFlags(search: string): RenderFlags {
  const params = new URLSearchParams(search);

  return {
    perf: flag(params, 'perf', DEFAULT_RENDER_FLAGS.perf),
    antialias: flag(params, 'aa', DEFAULT_RENDER_FLAGS.antialias),
    shadeBake: flag(params, 'shadebake', DEFAULT_RENDER_FLAGS.shadeBake),
    staticTerrain: flag(params, 'staticterrain', DEFAULT_RENDER_FLAGS.staticTerrain),
  };
}

export const RENDER_FLAGS: RenderFlags = parseRenderFlags(
  typeof globalThis.location === 'undefined' ? '' : globalThis.location.search,
);
